import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import Icon from '../../../icons/Icon';
import EditableFieldRow from '../../custom/EditableFieldRow';
import ExpandableGroup, { ExpandableGroupHeaderContext, useAccordionState } from '../../custom/ExpandableGroup';
import { AccordionIndicator, AccordionOpenCloseIcon } from '../../custom/AccordionIndicators';
import SarAccordionSectionHeader from './SarAccordionSectionHeader';
import SarValidationCategoryMapper from '../../../util/SarValidationCategoryMapper';
import { SarContactInfo, SarSupheliIslemAddress, SarValidationResult } from '../SarInterfaces';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { SarXmlTags } from '../../../constant/SarTagConstants';
import SarParser from '../SarParser';
import SUPHELIISLEM_TAGS = SarXmlTags.SUPHELIISLEM_TAGS;
import ADDRESS_TAGS = SarXmlTags.ADDRESS_TAGS;

const SupheliIslemlerAccordion = ({ xmlContent, mutateXmlContent, validate }: Props) => {
  const invalidFieldIds = useMemo<Set<string>>(() => {
    if (!validate || !validate.errors || validate.errors.length === 0) {
      return new Set<string>();
    }
    return SarValidationCategoryMapper.getInvalidFieldIds(validate.errors);
  }, [validate]);
  const supheliIslemler = useMemo(() => SarParser.parseSupheliIslemler(xmlContent), [xmlContent]);
  const accordion = useAccordionState();

  const errorsArray = validate?.errors ?? [];
  const errorsKey = errorsArray.map((e) => `${e.lineStart}-${e.lineEnd}-${e.message}`).join('|');
  const islemErrors = useMemo<Map<number, boolean>>(() => {
    const errors = new Map<number, boolean>();
    const currentInvalidFieldIds = errorsArray.length > 0 ? SarValidationCategoryMapper.getInvalidFieldIds(errorsArray) : new Set<string>();

    supheliIslemler.forEach((_, index) => {
      const islemPrefix = `supheliislem-${index}-`;
      const hasError = Array.from(currentInvalidFieldIds).some((fieldId) => {
        return fieldId.startsWith(islemPrefix);
      });
      errors.set(index, hasError);
    });
    return errors;
  }, [supheliIslemler.length, errorsKey]);

  const createGroupHeaderRenderer =
    (fieldPath: string) =>
    ({ label, hasError }: ExpandableGroupHeaderContext) => (
      <SarAccordionSectionHeader
        label={label}
        fieldPath={fieldPath}
        infoLabel={'Information'}
        triggerIcon='kyc_informationicon'
        triggerClassName='text-[#98A2B3]'
        hasError={hasError}
      />
    );

  const buildInfoConfig = (path: string, type: string = 'Metin') => ({ path, type, mode: 'bubble' as const });

  const handleFieldChange = useCallback(
    (islemIndex: number, tagName: string, value: string) => {
      mutateXmlContent((document) => {
        const islem = document.getElementsByTagName('supheliislem').item(islemIndex);
        if (!islem) return;
        let target = islem.getElementsByTagName(tagName).item(0);
        if (!target) {
          target = document.createElement(tagName);
          islem.appendChild(target);
        }
        target.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleAddressFieldChange = useCallback(
    (islemIndex: number, addressIndex: number, tagName: string, value: string) => {
      mutateXmlContent((document) => {
        const islem = document.getElementsByTagName('supheliislem').item(islemIndex);
        if (!islem) return;
        let addressesParent = islem.getElementsByTagName('adresler').item(0);
        if (!addressesParent) {
          addressesParent = document.createElement('adresler');
          islem.appendChild(addressesParent);
        }
        const addressNode = addressesParent.getElementsByTagName('adres').item(addressIndex);
        if (!addressNode) return;
        if (tagName === 'tip') {
          addressNode.setAttribute('tip', value);
          return;
        }
        let target = addressNode.getElementsByTagName(tagName).item(0);
        if (!target) {
          target = document.createElement(tagName);
          addressNode.appendChild(target);
        }
        target.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleContactInfoTipChange = useCallback(
    (islemIndex: number, contactIndex: number, tip: string) => {
      mutateXmlContent((document) => {
        const islem = document.getElementsByTagName('supheliislem').item(islemIndex);
        if (!islem) return;
        let contactsParent = islem.getElementsByTagName('iletisimbilgileri').item(0);
        if (!contactsParent) {
          contactsParent = document.createElement('iletisimbilgileri');
          islem.appendChild(contactsParent);
        }
        const contactNode = contactsParent.getElementsByTagName('iletisimbilgisi').item(contactIndex);
        if (!contactNode) return;
        contactNode.setAttribute('tip', tip);
      });
    },
    [mutateXmlContent],
  );

  const handleContactInfoValueChange = useCallback(
    (islemIndex: number, contactIndex: number, value: string) => {
      mutateXmlContent((document) => {
        const islem = document.getElementsByTagName('supheliislem').item(islemIndex);
        if (!islem) return;
        let contactsParent = islem.getElementsByTagName('iletisimbilgileri').item(0);
        if (!contactsParent) {
          contactsParent = document.createElement('iletisimbilgileri');
          islem.appendChild(contactsParent);
        }
        const contactNode = contactsParent.getElementsByTagName('iletisimbilgisi').item(contactIndex);
        if (!contactNode) return;
        contactNode.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const getIletisimTipiOptions = (): FormicaSelectBoxItem[] => [
    { label: 'İş Telefonu', value: 'is' },
    { label: 'Ev Telefonu', value: 'ev' },
    { label: 'Cep Telefonu', value: 'cep' },
    { label: 'Diğer Telefon', value: 'diger' },
    { label: 'Faks', value: 'faks' },
    { label: 'E-posta', value: 'eposta' },
  ];

  const getAracilikEtmeDurumuOptions = (): FormicaSelectBoxItem[] => [
    { label: 'AED-01  Banka aracılık etmiştir', value: 'AED-01' },
    { label: 'AED-02  Banka aracılık etmemiştir', value: 'AED-02' },
    { label: 'AED-03  Diğer Ödeme Kuruluşu aracılık etmiştir', value: 'AED-03' },
  ];

  const handleAddSupheliIslem = () => {
    mutateXmlContent((document) => {
      let parent = document.getElementsByTagName('supheliislemler').item(0);
      if (!parent) {
        parent = document.createElement('supheliislemler');
        document.documentElement.appendChild(parent);
      }
      const islem = document.createElement('supheliislem');
      SUPHELIISLEM_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        islem.appendChild(element);
      });
      islem.appendChild(document.createElement('adresler'));
      islem.appendChild(document.createElement('iletisimbilgileri'));
      parent.appendChild(islem);
    });
  };

  const handleAddAddress = (islemIndex: number) => {
    mutateXmlContent((document) => {
      const islem = document.getElementsByTagName('supheliislem').item(islemIndex);
      if (!islem) return;
      let addressesParent = islem.getElementsByTagName('adresler').item(0);
      if (!addressesParent) {
        addressesParent = document.createElement('adresler');
        islem.appendChild(addressesParent);
      }
      const addressNode = document.createElement('adres');
      addressNode.setAttribute('tip', '');
      ADDRESS_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        addressNode.appendChild(element);
      });
      addressesParent.appendChild(addressNode);
    });
  };

  const handleAddContact = (islemIndex: number) => {
    mutateXmlContent((document) => {
      const islem = document.getElementsByTagName('supheliislem').item(islemIndex);
      if (!islem) return;
      let contactsParent = islem.getElementsByTagName('iletisimbilgileri').item(0);
      if (!contactsParent) {
        contactsParent = document.createElement('iletisimbilgileri');
        islem.appendChild(contactsParent);
      }
      const contactNode = document.createElement('iletisimbilgisi');
      contactNode.setAttribute('tip', '');
      contactNode.textContent = '';
      contactsParent.appendChild(contactNode);
    });
  };

  return (
    <div className={'h-full w-full pr-6 pt-3 2xl:pt-4 flex flex-col gap-y-3'}>
      <div className={'flex items-center justify-between'}>
        <div className={'text-[#1D2939] text-base font-semibold'}>XML Document</div>
        <button
          onClick={handleAddSupheliIslem}
          type='button'
          className='w-5 h-5 hover:bg-f-gray-2 rounded-sm flex items-center justify-center transition-all duration-200'
        >
          <Icon icon='add' className='text-[#1D2939]' />
        </button>
      </div>
      {supheliIslemler.length === 0 ? (
        <div className={'rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-6 py-10 text-center text-sm text-[#667085]'}>
          Henüz şüpheli işlem bulunmuyor.
        </div>
      ) : (
        <div className={'flex flex-col border rounded-[10px] h-full overflow-y-auto overflow-hidden'}>
          {supheliIslemler.map((islem, index) => {
            const islemKey = `supheli-islem-${index}`;
            const expanded = accordion.isExpanded(islemKey);
            const hasError = islemErrors.get(index) ?? false;
            return (
              <div key={islem.id} className={'bg-white rounded-t-xl rounded-b-xl'}>
                <div
                  role={'button'}
                  tabIndex={0}
                  className={`flex w-full rounded-t-xl items-center justify-between px-5 2xl:px-6 h-[56px] 2xl:h-[58px] text-left border-b ${index === supheliIslemler.length - 1 ? '' : 'border-b'}`}
                  onClick={() => accordion.toggle(islemKey)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      accordion.toggle(islemKey);
                    }
                  }}
                >
                  <div className='flex items-center gap-x-3'>
                    <div>
                      <AccordionOpenCloseIcon expanded={expanded} hasError={hasError} />
                    </div>
                    <SarAccordionSectionHeader
                      label={`Şüpheli İşlem ${index + 1}`}
                      fieldPath={'supheliIslem'}
                      infoLabel='Information'
                      hasError={hasError}
                      triggerIcon='kyc_informationicon'
                      triggerClassName={hasError ? 'text-f-red-1' : 'text-[#667085]'}
                    />
                  </div>
                  <AccordionIndicator expanded={expanded} />
                </div>
                {expanded && (
                  <div className={'flex flex-col divide-y divide-[#F2F4F7] border-b'}>
                    <EditableFieldRow
                      label={'İşlem Çeşidi'}
                      value={islem.transactionType}
                      onChange={(value) => handleFieldChange(index, 'islemcesidi', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemcesidi`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemCesidi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İşlem Tarihi 1'}
                      value={islem.transactionDate1}
                      onChange={(value) => handleFieldChange(index, 'islemtarihi1', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemtarihi1`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemTarihi', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'İşlem Tarihi 2'}
                      value={islem.transactionDate2}
                      onChange={(value) => handleFieldChange(index, 'islemtarihi2', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemtarihi2`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemTarihi2', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'İşlem Durumu'}
                      value={islem.transactionStatus}
                      onChange={(value) => handleFieldChange(index, 'islemdurumu', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemdurumu`)}
                      infoConfig={buildInfoConfig('supheliIslem.durum', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Hesaba Bağlı'}
                      value={islem.relatedHesap}
                      onChange={(value) => handleFieldChange(index, 'hesababagli', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-hesababagli`)}
                      infoConfig={buildInfoConfig('supheliIslem.hesabaBagli', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Tutar Tipi'}
                      value={islem.amountType}
                      onChange={(value) => handleFieldChange(index, 'tutartp', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-tutartp`)}
                      infoConfig={buildInfoConfig('supheliIslem.tutarTP')}
                    />
                    <EditableFieldRow
                      label={'Tutar'}
                      value={islem.amount}
                      onChange={(value) => handleFieldChange(index, 'tutar', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-tutar`)}
                      infoConfig={buildInfoConfig('supheliIslem.tutar')}
                    />
                    <EditableFieldRow
                      label={'Para Birimi'}
                      value={islem.currency}
                      onChange={(value) => handleFieldChange(index, 'parabirimi', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-parabirimi`)}
                      infoConfig={buildInfoConfig('supheliIslem.paraBirimi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İşlem Türü'}
                      value={islem.transactionCategory}
                      onChange={(value) => handleFieldChange(index, 'islemturu', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemturu`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemTuru')}
                    />
                    <EditableFieldRow
                      label={'İşlem Kanalı'}
                      value={islem.channel}
                      onChange={(value) => handleFieldChange(index, 'islemkanali', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemkanali`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemKanali')}
                    />
                    <EditableFieldRow
                      label={'IPv4'}
                      value={islem.ipv4}
                      onChange={(value) => handleFieldChange(index, 'ipv4', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-ipv4`)}
                      infoConfig={buildInfoConfig('supheliIslem.ipv4')}
                    />
                    <EditableFieldRow
                      label={'Port'}
                      value={islem.port}
                      onChange={(value) => handleFieldChange(index, 'port', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-port`)}
                      infoConfig={buildInfoConfig('supheliIslem.port')}
                    />
                    <EditableFieldRow
                      label={'İşlem Açıklaması'}
                      value={islem.description}
                      multiline
                      onChange={(value) => handleFieldChange(index, 'islemaciklamasi', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemaciklamasi`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemAciklamasi')}
                    />
                    <EditableFieldRow
                      label={'Ülke Kodu'}
                      value={islem.countryCode}
                      onChange={(value) => handleFieldChange(index, 'ulkekodu', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-ulkekodu`)}
                      infoConfig={buildInfoConfig('supheliIslem.ulkeKodu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İl Kodu'}
                      value={islem.cityCode}
                      onChange={(value) => handleFieldChange(index, 'ilkodu', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-ilkodu`)}
                      infoConfig={buildInfoConfig('supheliIslem.ilKodu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İlçe Kodu'}
                      value={islem.districtCode}
                      onChange={(value) => handleFieldChange(index, 'ilcekodu', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-ilcekodu`)}
                      infoConfig={buildInfoConfig('supheliIslem.ilceKodu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İşlemin Gerçekleştiği Yer'}
                      value={islem.executionLocation}
                      onChange={(value) => handleFieldChange(index, 'islemingerceklestirildigiyer', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemingerceklestirildigiyer`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemGerceklestirildigiYer')}
                    />
                    <EditableFieldRow
                      label={'İşlemin Tetiklendiği Yer'}
                      value={islem.triggerLocation}
                      onChange={(value) => handleFieldChange(index, 'islemintetiklendigiyer', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemintetiklendigiyer`)}
                      infoConfig={buildInfoConfig('supheliIslem.isleminTetiklendigiYer')}
                    />
                    <EditableFieldRow
                      label={'İşleme Aracılık Etme Durumu'}
                      value={islem.mediationStatus}
                      onChange={(value) => handleFieldChange(index, 'islemearaciliketmedurumu', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemearaciliketmedurumu`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemeAracilikEtmeDurumu')}
                      customOptions={getAracilikEtmeDurumuOptions()}
                    />
                    <EditableFieldRow
                      label={'İşleme Aracılık Eden Kurum'}
                      value={islem.mediationInstitution}
                      onChange={(value) => handleFieldChange(index, 'islemearacilikedenkurum', value)}
                      hasError={invalidFieldIds.has(`supheliislem-${index}-islemearacilikedenkurum`)}
                      infoConfig={buildInfoConfig('supheliIslem.islemeAracilikEdenKurum')}
                    />
                    <ExpandableGroup<SarSupheliIslemAddress>
                      baseKey={`${islemKey}-addresler`}
                      label={'Adresler'}
                      items={islem.addresses}
                      accordion={accordion}
                      getItemTitle={(_, addressIndex) => `Adres ${addressIndex + 1}`}
                      onAdd={() => handleAddAddress(index)}
                      renderHeaderDetails={createGroupHeaderRenderer('supheliIslem.adresler')}
                      hasError={
                        invalidFieldIds.has(`supheliislem-${index}-adresler`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`supheliislem-${index}-adres-`))
                      }
                      getItemHasError={(_, addressIndex) => {
                        return Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`supheliislem-${index}-adres-${addressIndex}-`));
                      }}
                      renderItems={(address, addressIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl'}>
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Adres Tipi'}
                            value={address.type}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'tip', value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-adres-${addressIndex}-tip`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Adres Satırı'}
                            value={address.line}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'adressatir', value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-adres-${addressIndex}-adressatir`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.adresSatir')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Posta Kodu'}
                            value={address.postalCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'postakodu', value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-adres-${addressIndex}-postakodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.postaKodu')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'İl Kodu'}
                            value={address.cityCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ilkodu', value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-adres-${addressIndex}-ilkodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.ilKodu', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'İlçe Kodu'}
                            value={address.districtCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ilcekodu', value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-adres-${addressIndex}-ilcekodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.ilceKodu', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Ülke Kodu'}
                            value={address.countryCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ulkekodu', value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-adres-${addressIndex}-ulkekodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.ulkeKodu', 'Kod')}
                          />
                        </div>
                      )}
                      emptyLabel={'Adres bilgisi bulunamadı.'}
                    />
                    <ExpandableGroup<SarContactInfo>
                      baseKey={`${islemKey}-contact`}
                      label={'İletişim Bilgileri'}
                      items={islem.contactInfos}
                      accordion={accordion}
                      getItemTitle={(_, contactIndex) => `İletişim Bilgisi ${contactIndex + 1}`}
                      onAdd={() => handleAddContact(index)}
                      renderHeaderDetails={createGroupHeaderRenderer('supheliIslem.iletisimBilgileri')}
                      hasError={
                        invalidFieldIds.has(`supheliislem-${index}-iletisimbilgileri`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`supheliislem-${index}-iletisimbilgisi-`))
                      }
                      renderItems={(contact, contactIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl pl-12'}>
                          <EditableFieldRow
                            label={'Tip'}
                            value={contact.tip}
                            onChange={(value) => handleContactInfoTipChange(index, contactIndex, value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-iletisimbilgisi-${contactIndex}-tip`)}
                            infoConfig={buildInfoConfig('supheliIslem.iletisimBilgileri.tip', 'Kod')}
                            customOptions={getIletisimTipiOptions()}
                          />
                          <EditableFieldRow
                            label={'Değer'}
                            value={contact.value}
                            onChange={(value) => handleContactInfoValueChange(index, contactIndex, value)}
                            hasError={invalidFieldIds.has(`supheliislem-${index}-iletisimbilgisi-${contactIndex}`)}
                          />
                        </div>
                      )}
                      emptyLabel={'İletişim bilgisi bulunamadı.'}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface Props {
  xmlContent?: string;
  mutateXmlContent: (mutator: (document: Document) => void) => void;
  validate?: SarValidationResult;
}

export default SupheliIslemlerAccordion;
